// src/pages/CartPage.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useCart } from '../contextPage/CartContext';
import { useNavigate } from 'react-router-dom';
import { API } from '../services/api';
import './CartPage.css';
import Footer from '../components/Footer/Footer';

const getProductId = (item) => String(item.product_id ?? item.productId ?? item.id);

const parseImages = (images) => {
    try {
        if (Array.isArray(images)) return images;
        if (typeof images === 'string') return JSON.parse(images || '[]');
        return [];
    } catch {
        return [];
    }
};

const getCartItemImages = (item) => {
    const a = parseImages(item.images);
    if (Array.isArray(a) && a.length > 0) return a;
    return parseImages(item.product?.images) ?? [];
};

const getCartItemName = (item) =>
    item.productName ?? item.product?.productName ?? item.name ?? item.product?.name ?? 'Product';

// normalize the item so BuyNowPage always gets product.id
const toBuyNowProduct = (item) => {
    const pid = Number(getProductId(item));
    // Keep other fields (name, cost, images, etc.) as-is
    return { ...item, id: pid };
};

const CartPage = () => {
    const { cart, updateQuantity, removeFromCart, getCartItemUnitPrice } = useCart();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');

    const getUnitPrice = (item) =>
        (typeof getCartItemUnitPrice === 'function'
            ? getCartItemUnitPrice(item)
            : Number(item.unitPrice ?? item.price ?? item.cost ?? item.displayPrice ?? 0));

    // Filter cart by search (only cart products, not main product list)
    const filteredCart = useMemo(() => {
        if (!cart || !searchQuery.trim()) return cart || [];
        const q = searchQuery.trim().toLowerCase();
        return cart.filter((item) => {
            const name = getCartItemName(item);
            const id = getProductId(item);
            return name.toLowerCase().includes(q) || id.toLowerCase().includes(q);
        });
    }, [cart, searchQuery]);

    // initial qty map by productId
    const initialQty = useMemo(() => {
        const q = {};
        (cart || []).forEach((item) => {
            const pid = getProductId(item);
            q[pid] = item.quantity || 1;
        });
        return q;
    }, [cart]);

    const [quantities, setQuantities] = useState(initialQty);

    // keep local state in sync when cart changes
    useEffect(() => {
        setQuantities(initialQty);
    }, [initialQty]);

    const handleQtyChange = (productId, value) => {
        const next = Math.max(1, parseInt(value, 10) || 1);
        setQuantities((prev) => ({ ...prev, [productId]: next }));
        updateQuantity(productId, next); // absolute set on server
    };

    if (!cart || cart.length === 0) {
        return <p className="avn-cart-empty">🛒 Your cart is empty!</p>;
    }

    return (
        <>
            <div className="avn-cart-container">
                <h2 className="avn-cart-title">Your Cart</h2>

                <div className="avn-cart-search-wrap">
                    <input
                        type="text"
                        className="avn-cart-search"
                        placeholder="Search in your cart..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label="Search cart products"
                    />
                    {searchQuery.trim() && (
                        <span className="avn-cart-search-hint">
                            Showing {filteredCart.length} of {cart.length} items
                        </span>
                    )}
                </div>

                {filteredCart.length === 0 ? (
                    <p className="avn-cart-empty avn-cart-no-match">
                        No cart items match &quot;{searchQuery.trim()}&quot;. Try a different search.
                    </p>
                ) : (
                filteredCart.map((item) => {
                    const productId = getProductId(item);
                    const imgs = getCartItemImages(item);
                    const imageUrl = Array.isArray(imgs) && imgs.length > 0 ? `${API.imagePath}${imgs[0]}` : null;
                    const qty = quantities[productId] ?? item.quantity ?? 1;
                    const unitPrice = getUnitPrice(item);
                    const lineTotal = unitPrice * qty;
                    const displayName = getCartItemName(item);

                    return (
                        <div key={item.cart_item_id ?? productId} className="avn-cart-item">
                            {/* LEFT SIDE */}
                            <div
                                className="avn-cart-left"
                                onClick={() => navigate(`/product/${productId}`)}
                            >
                                <img
                                    src={imageUrl || '/placeholder.jpg'}
                                    alt={displayName}
                                    className="avn-cart-image"
                                    onError={(e) => (e.currentTarget.src = "/placeholder.jpg")}
                                />
                                <div className="avn-cart-info">
                                    <h4 className="avn-cart-name">{displayName}</h4>
                                    {/* <div className="si">
                                        <p className="avn-cart-price">
                                            ₹{unitPrice.toLocaleString()} {qty > 1 && <span>× {qty}</span>}
                                        </p>
                                    </div> */}
                                    {item.size && (
                                        <p className="avn-cart-size">
                                            Size: {item.size}
                                        </p>
                                    )}
                                    <p className="avn-cart-total">
                                        Total: <strong>₹{lineTotal.toLocaleString()}</strong>
                                    </p>
                                    {/* <div className="avn-cart-qty">
                                        <label htmlFor={`qty-${productId}`}>Qty:</label>
                                        <input
                                            id={`qty-${productId}`}
                                            type="number"
                                            min="1"
                                            value={qty}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => handleQtyChange(productId, e.target.value)}
                                        />
                                    </div> */}
                                </div>
                            </div>

                            {/* RIGHT SIDE ACTIONS */}
                            <div className="avn-cart-actions">
                                <button
                                    className="avn-cart-remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFromCart(productId, item.variant_id);
                                    }}
                                >
                                    Remove
                                </button>

                                <button
                                    className="avn-cart-buy-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const buyProduct = toBuyNowProduct(item); // ensure product.id exists
                                        navigate("/buy-now", {
                                            state: {
                                                product: buyProduct,
                                                variant_id: item.variant_id,
                                                quantity: item.quantity,
                                            },
                                        });
                                    }}
                                >
                                    Buy Now
                                </button>
                            </div>
                        </div>
                    );
                })
                )}
            </div>
            <Footer />
        </>
    );
};

export default CartPage;
